import type { Tag } from '@awork/_shared/models/tag.model'
import type {
  ProjectStatusMappingEntry,
  TaskStatusMappingEntry
} from '@awork/features/workflow/models/link-workflow-form.model'

export interface ProjectTypeTaskBundles {
  taskBundleIds: string[]
  projectTypeId: string
  projectStatuses: {
    taskBundleIds: string[]
    projectStatusId: string
  }[]
}

export interface IDeleteProjectRoleBody {
  newProjectRoleId?: string
}

export interface IDeleteProjectTypeBody {
  newProjectTypeId?: string
}

export interface IProjectDeleteBody {
  deleteTimeTrackings: boolean
}

export interface IRemoveProjectRoleBody {
  oldProjectRoleId?: string
  newProjectRoleId?: string
}

export interface IDeleteProjectStatusBody {
  projectStatusId: string
}

export interface ProjectTemplateImportOptions {
  name: string
  importProjectDetails?: boolean
  importProjectSettings?: boolean
  importProjectStatuses?: boolean
  importAutomations?: boolean
  importTaskStatuses?: boolean
  importTaskAndLists?: boolean
  importTimeBudget?: boolean
  referenceDate?: Date
}

export interface CopyProjectForm {
  copyProjectFiles?: boolean
  copyProjectDocuments?: boolean
  copyProjectMembers?: boolean
  copyTaskAssignees?: boolean
  copyTaskStatuses?: boolean
  copyWatchers?: boolean
  resetCustomFieldValues?: boolean
  nameOverride?: string
  operationId?: string
}

export interface CopyProjectResponse {
  projectId: string
  operationId: string
}

export interface ProjectKeyReference {
  id: string
  isExternal: boolean
}

export const CONNECT_ACCESS_RESTRICTION_SAME_DOMAIN = 'connect-access-restriction-same-domain'

export enum ProjectKeyValidationError {
  AlreadyInUse = 'project-key-already-in-use',
  InvalidPattern = 'project-key-invalid-pattern',
  InvalidLength = 'project-key-invalid-length'
}

export interface ProjectBatchChangeStatus {
  projectStatusMapping: ProjectStatusMappingEntry[]
}

export interface ProjectBatchChangeProjectType {
  projectTypeId: string
}

export interface ProjectBatchLinkWorkflow {
  workflowId: string
  projectStatusMapping?: ProjectStatusMappingEntry[]
  taskStatusMapping?: TaskStatusMappingEntry[]
  keepCustomFields?: boolean
  keepCustomAutomations?: boolean
}

export interface ProjectBatchSetClient {
  companyId: string
}

export interface ProjectBatchSetProjectLead {
  responsibleUserId: string
}

export interface ProjectBatchSetTeams {
  teamIds: string[]
}

export interface ProjectBatchAdjustDates {
  daysOffset: number
  adjustTasks?: boolean
  adjustMilestones?: boolean
  adjustBookings?: boolean
  adjustDatesOfDoneTasks?: boolean
}

export interface ProjectBatchTags {
  tags?: Tag[]
  tagsToRemove?: { name: string }[]
}

export type ProjectBatchRequestBody =
  | IProjectDeleteBody
  | ProjectBatchAdjustDates
  | ProjectBatchSetClient
  | ProjectBatchChangeProjectType
  | ProjectBatchChangeStatus
  | ProjectBatchLinkWorkflow
  | ProjectBatchSetProjectLead
  | ProjectBatchSetTeams
  | ProjectBatchTags
  | Record<string, never>

export enum ProjectBatchOperation {
  changeProjectStatus = 'changeProjectStatus',
  changeProjectType = 'changeProjectType',
  linkWorkflow = 'linkWorkflow',
  setClient = 'setClient',
  setProjectLead = 'setProjectLead',
  addTeams = 'addTeams',
  removeTeams = 'removeTeams',
  adjustDates = 'adjustDates',
  addTags = 'addTags',
  removeTags = 'removeTags',
  delete = 'delete'
}

export const projectBatchURLMap: Record<ProjectBatchOperation, string> = {
  [ProjectBatchOperation.changeProjectStatus]: 'changeprojectstatus',
  [ProjectBatchOperation.changeProjectType]: 'changeprojecttype',
  [ProjectBatchOperation.linkWorkflow]: 'linkworkflow',
  [ProjectBatchOperation.setClient]: 'setclient',
  [ProjectBatchOperation.setProjectLead]: 'setprojectlead',
  [ProjectBatchOperation.addTeams]: 'addteams',
  [ProjectBatchOperation.removeTeams]: 'removeteams',
  [ProjectBatchOperation.adjustDates]: 'adjustdates',
  [ProjectBatchOperation.addTags]: 'addtags',
  [ProjectBatchOperation.removeTags]: 'removetags',
  [ProjectBatchOperation.delete]: 'delete'
}
